import { useState, useEffect, useRef } from "react";
import { useTrackContext } from "@/state/TrackContext";
import { addTrackToOrder, syncTrackOrderWithCart } from "../utils/trackOrderStorage";
import { TRACK_KEYS } from "../utils/trackHelpers";

// Helper to extract track keys from cart items
// Handles both Shopify native cart structure and headless cart structure
const extractTrackKeysFromCart = (items: any[]): string[] => {
  const trackKeys = new Set<string>();
  
  items.forEach((item) => {
    // Handle both Shopify native cart (product_title) and headless cart (merchandise.product.title)
    const shopifyTitle = item.product_title || item.title || "";
    const headlessTitle = item.merchandise?.product?.title || "";
    const variantTitle = item.merchandise?.title || item.variant_title || "";
    const title = (shopifyTitle || headlessTitle || variantTitle).toLowerCase();
    
    // Handle both Shopify native cart (product_type) and headless cart (merchandise.product.productType)
    const shopifyProductType = item.product_type || "";
    const headlessProductType = item.merchandise?.product?.productType || "";
    const productType = (shopifyProductType || headlessProductType).toLowerCase();
    
    // Arcade detection
    if (
      productType.includes("arcade") ||
      title.includes("arcade card") ||
      title.includes("arcade game") ||
      title.includes("pre-paid arcade") ||
      title.includes("prepaid arcade") ||
      (title.includes("arcade") && (title.includes("pre-paid") || title.includes("prepaid")))
    ) {
      trackKeys.add(TRACK_KEYS.ARCADE);
      return;
    }
    
    // BattleKart detection
    if (productType.includes("battlekart") || title.includes("battlekart") || title.includes("battle kart") || title.includes("battle-kart")) {
      trackKeys.add(TRACK_KEYS.BK);
      return;
    }
    
    // Membership detection
    if (productType.includes("membership") || title.includes("membership")) {
      trackKeys.add(TRACK_KEYS.MEMBERSHIP);
      return;
    }
    
    // Junior Track detection
    if (title.includes("junior")) {
      trackKeys.add(TRACK_KEYS.JUNIOR);
      return;
    }
    
    // Intermediate Track detection
    if (title.includes("intermediate")) {
      trackKeys.add(TRACK_KEYS.INTERMEDIATE);
      return;
    }
    
    // Mini Track detection
    if (title.includes("mini")) {
      trackKeys.add(TRACK_KEYS.MINI);
      return;
    }
    
    // Main Track detection
    if (title.includes("main track") || title.includes("main-track")) {
      trackKeys.add(TRACK_KEYS.MAIN);
      return;
    }
    
    // Default: Check if it's a HyperKarting session product (has date in title like "23rd Jan")
    if (title.match(/\d+(st|nd|rd|th)\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i)) {
      // This is likely a session product - try to determine track from other fields
      // Default to MAIN if no other track detected
      trackKeys.add(TRACK_KEYS.MAIN);
      return;
    }
  });
  
  return Array.from(trackKeys);
};

// Helper to update track order - adds new tracks and removes ones no longer in cart
const updateTrackOrder = (trackKeys: string[], previousTrackKeysRef: React.MutableRefObject<Set<string>>) => {
  // Don't process if no tracks were detected
  if (trackKeys.length === 0) {
    return;
  }
  
  const previousTrackKeys = previousTrackKeysRef.current;
  
  // Find newly added tracks (tracks that weren't in the previous cart)
  const newTracks = trackKeys.filter((key) => !previousTrackKeys.has(key));
  
  // Add each new track to the order (in the order they appear)
  newTracks.forEach((trackKey) => {
    addTrackToOrder(trackKey);
  });
  
  // Sync to remove any tracks that are no longer in cart
  syncTrackOrderWithCart(trackKeys);
  
  // Update the ref with current track keys
  previousTrackKeysRef.current = new Set(trackKeys);
};

export const useCartData = () => {
  const { productRenderedLocation, headlessCart, isShopifyCartVariantsLoaded, dispatch } = useTrackContext();
  const [cartData, setCartData] = useState<any[]>([]);
  const previousTrackKeysRef = useRef<Set<string>>(new Set()); // Track previously seen tracks in cart

  useEffect(() => {
    if (productRenderedLocation === "external") {
      setCartData(headlessCart);
      // Update track order with headless cart
      if (headlessCart.length > 0) {
        const trackKeys = extractTrackKeysFromCart(headlessCart);
        updateTrackOrder(trackKeys, previousTrackKeysRef);
      }
    } else {
      const fetchCart = async () => {
        try {
          const response = await fetch("/cart.json");
          const data = await response.json();
          const items = data.items || [];
          setCartData(items);
          
          // Update track order with fetched cart data
          if (items.length > 0) {
            const trackKeys = extractTrackKeysFromCart(items);
            updateTrackOrder(trackKeys, previousTrackKeysRef);
          }

          // Sync global state with fetched cart data so the total
          // is correct on page refresh without waiting for other hooks.
          // Prefer `total_price` (nets order/cart-level discounts) over
          // `items_subtotal_price` (line-level only) so the displayed Total matches
          // the session-pill Sub Totals, which also net all discounts.
          const cartTotal = data.total_price || data.items_subtotal_price;
          if (items.length > 0 && cartTotal) {
            dispatch({
              type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
              payload: { headlessCartTotalAmount: cartTotal },
            });
            dispatch({
              type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
              payload: { isAnyItemsAvailableInCart: true },
            });
          }
        } catch (error) {
          console.error("Error fetching cart data:", error);
          setCartData([]);
        }
      };

      // Always fetch cart data
      fetchCart();

      const handleCartUpdate = (event: CustomEventInit) => {
        const data = event.detail;
        if (data?.cart) {
          const items = data.cart.items || [];
          setCartData(items);
          
          // Update track order when cart is updated (after add to cart)
          if (items.length > 0) {
            const trackKeys = extractTrackKeysFromCart(items);
            updateTrackOrder(trackKeys, previousTrackKeysRef);
          }
        }
      };

      window.addEventListener("cart:updated", handleCartUpdate);
      return () => {
        window.removeEventListener("cart:updated", handleCartUpdate);
      };
    }
  }, [productRenderedLocation, headlessCart, isShopifyCartVariantsLoaded, dispatch]);

  return cartData;
};
